import {
    DTOCardFormField,
} from 'app/types/form.types'
import {
    LongTermLeaseType,
} from 'app/Apps/OrderManagement/LongTermLease/longTermLease.types'
import {
    ContractCurrency, IncoTerm,
    LaneGroupTemperatureRange,
    OrderStatuses, PaymentTerms, ProductType,
} from 'app/enums/label.enums'
import {
    BillingCompany,
} from 'app/Apps/ContactManagement/BillingCompanies/BillingCompanyForm.types'
import {
    LongTermLeaseInvoiceType,
} from 'app/requests/LongTermLeaseInvoice/LongTermLeaseInvoice.types'
import {
    LongTermLeaseFeesType,
} from 'app/Apps/Pricing/ContractBasis/LongTermLeaseFees/LongTermLeaseFees.types'

const getGeneralInfoFields = ({
    getAllowedValues,
    longTermLeaseFees,
    longTermLease,
    billingCompany,
}: {
    getAllowedValues: (enumType: Record<string, string>) => Record<string, string>,
    longTermLeaseFees: LongTermLeaseFeesType,
    longTermLease: LongTermLeaseType,
    billingCompany: BillingCompany,
}): DTOCardFormField<keyof (
    LongTermLeaseInvoiceType & LongTermLeaseType & LongTermLeaseFeesType & BillingCompany)>[] => {
    return [
        {
            title: 'Contract Info',
            className: 'contractInfo',
            id: 'contractInfo',
            fields: [
                {
                    name: 'longTermLeaseNumber',
                    value: longTermLease.longTermLeaseNumber,
                    componentName: 'InputSingleline',
                    labelKey: 'CONTRACT_NUMBER',
                },
                {
                    name: 'tempRange',
                    value: longTermLease.tempRange,
                    componentName: 'MultiEnumSelector',
                    allowedValues: getAllowedValues(LaneGroupTemperatureRange),
                    labelKey: 'TEMPERATURE_RANGE',
                },
                {
                    name: 'createdBy',
                    value: longTermLease.createdBy,
                    componentName: 'InputSingleline',
                    labelKey: 'CREATED_BY',
                },
            ],
        },
        {
            title: 'Summary',
            className: 'summary',
            id: 'summary',
            fields: [
                {
                    id: 'summary-column1',
                    fields: [
                        {
                            name: 'leaseStartTimestamp',
                            value: longTermLease.leaseStartTimestamp,
                            componentName: 'DateTimeZonedSelect',
                            labelKey: 'LEASE_START_TIMESTAMP',
                        },
                        {
                            name: 'baseLeaseDays',
                            value: longTermLease.baseLeaseDays,
                            componentName: 'InputSingleline',
                            labelKey: 'BASE_LEASE_DAYS',
                        },
                        {
                            name: 'leaseEndTimestamp',
                            value: longTermLease.leaseEndTimestamp,
                            componentName: 'InputSingleline',
                            labelKey: 'LEASE_END_TIMESTAMP',
                        },
                        {
                            name: 'currency',
                            value: longTermLeaseFees?.currency,
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(ContractCurrency),
                            labelKey: 'CURRENCY',
                        },
                        {
                            name: 'requiredContainers',
                            value: longTermLease?.requiredContainers,
                            componentName: 'RequiredContainerByProductTypeList',
                            allowedProductTypes: Object.keys(ProductType) as ProductType[],
                            labelKey: 'DEDICATED_CONTAINERS',
                        },
                    ],
                },
                {
                    id: 'summary-column2',
                    fields: [
                        {
                            name: 'status',
                            value: longTermLease.status,
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(OrderStatuses),
                            labelKey: 'STATUS',
                        },
                        {
                            name: 'pricingAccount',
                            value: longTermLease.pricingAccount,
                            componentName: 'AccountSelector',
                            labelKey: 'PRICING_ACCOUNT',
                        },
                        {
                            name: 'customerExecution',
                            value: longTermLease.customerExecution,
                            componentName: 'LocationExpandedViewObjectSelector',
                            labelKey: 'CUSTOMER_QUOTATION',
                        },
                        {
                            name: 'incoTerm',
                            value: longTermLease.incoTerm,
                            componentName: 'Select',
                            allowedValues: getAllowedValues(IncoTerm),
                            labelKey: 'INCOTERM',
                        },
                        {
                            name: 'incoTermLocation',
                            value: longTermLease.incoTermLocation,
                            componentName: 'LocationObjectSelector',
                            labelKey: 'INCOTERM_LOCATION',
                        },
                    ],
                },
            ],
        },
        {
            title: 'Billing Information',
            className: 'billingInformation',
            id: 'billingInformation',
            fields: [
                {
                    name: 'billingCompany',
                    value: longTermLeaseFees?.billingCompany,
                    componentName: 'BillingCompanyObjectSelector',
                    labelKey: 'BILLING_COMPANY',
                },
                {
                    name: 'paymentTerms',
                    value: billingCompany?.paymentTerms,
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(PaymentTerms),
                    labelKey: 'PAYMENT_TERMS',
                },
                {
                    name: 'sapBusinessPartnerNumber',
                    value: billingCompany?.sapBusinessPartnerNumber,
                    componentName: 'LongText',
                    labelKey: 'SAP_BUSINESS_PARTNER_NUMBER',
                },
            ],
        },
        {
            title: 'Comments',
            className: 'comments',
            id: 'comments',
            fields: [
                {
                    id: 'comment-column1',
                    fields: [{
                        name: 'commentForBilling',
                        value: longTermLeaseFees?.commentForBilling,
                        componentName: 'LongText',
                        labelKey: 'COMMENT_FOR_BILLING_PRICING',
                    }],
                },
                {
                    id: 'comment-column2',
                    fields: [
                        {
                            name: 'instructionsForBilling',
                            value: billingCompany?.instructionsForBilling,
                            componentName: 'LongText',
                            labelKey: 'INSTRUCTIONS_FOR_BILLING_COMMERCIAL',
                        },
                        {
                            name: 'commentForBillingOps',
                            value: longTermLease.commentForBillingOps,
                            componentName: 'LongText',
                            labelKey: 'COMMENT_FOR_BILLING_OPS',
                        },
                    ],
                },
            ],
        },
    ]
}

export default getGeneralInfoFields
